import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import {
    PostWithInteractions,
    SocialResponse,
    CreatePostParams,
    UpdatePostParams,
    DeletePostParams
} from './types';

/**
 * Create a new post
 * @param params CreatePostParams object with post data
 * @returns Promise with created post or null if failed
 */
export const createPost = async (params: CreatePostParams): Promise<PostWithInteractions | null> => {
    try {
        const res = await api.post<SocialResponse<PostWithInteractions>>('/social/post', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to create post:', e);
        return null;
    }
};

/**
 * Get a specific post by ID
 * @param postid Post ID to retrieve
 * @returns Promise with post or null if not found
 */
export const getPost = async (postid: string): Promise<PostWithInteractions | null> => {
    try {
        const res = await api.get<SocialResponse<PostWithInteractions[]>>('/social/post', {
            params: { postid },
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data?.[0] ?? null) : null;
    } catch (e) {
        console.error(`Failed to get post ${postid}:`, e);
        return null;
    }
};

/**
 * List posts with filtering options
 * @param params Object with filtering and pagination options
 * @returns Promise with array of posts
 */
export const listPosts = async (params: {
    userid?: string;
    parent_postid?: string;
    limit?: number;
    offset?: number;
}): Promise<PostWithInteractions[] | null> => {
    try {
        const res = await api.get<SocialResponse<PostWithInteractions[]>>('/social/post', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? []) : null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.code === 404) {
            // Handle not found case. it's not an error, just no posts found
            console.warn('No posts found for the given parameters');
            return [];
        }

        console.error('Failed to list posts:', e);
        return null;
    }
};

/**
 * Update an existing post (author only)
 * @param params UpdatePostParams object with post updates
 * @returns Promise with updated post or null if failed
 */
export const updatePost = async (params: UpdatePostParams): Promise<PostWithInteractions | null> => {
    try {
        const res = await api.put<SocialResponse<PostWithInteractions>>('/social/post', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update post:', e);
        return null;
    }
};

/**
 * Update an existing post using PATCH method (author only)
 * @param params UpdatePostParams object with post updates
 * @returns Promise with updated post or null if failed
 */
export const patchPost = async (params: UpdatePostParams): Promise<PostWithInteractions | null> => {
    try {
        const res = await api.patch<SocialResponse<PostWithInteractions>>('/social/post', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to patch post:', e);
        return null;
    }
};

/**
 * Delete a post (author only)
 * @param params DeletePostParams object with postid
 * @returns Promise with boolean indicating success
 */
export const deletePost = async (params: DeletePostParams): Promise<boolean> => {
    try {
        const res = await api.delete<SocialResponse<{message: string}>>('/social/post', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to delete post:', e);
        return false;
    }
};

/**
 * Search posts by query, hashtag, or location
 * @param params Object with search parameters
 * @returns Promise with posts array or null if failed
 */
export const searchPosts = async (params: {
    query?: string;
    hashtag?: string;
    addressid?: string;
    limit?: number;
    offset?: number;
}): Promise<PostWithInteractions[] | null> => {
    try {
        const res = await api.get<SocialResponse<{
            user?: any;
            posts: PostWithInteractions[];
            hashtags?: any[];
            users?: any[];
        }>>('/social/search', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? (res.data.data?.posts ?? []) : null;
    } catch (e) {
        console.error('Failed to search posts:', e);
        return null;
    }
};